// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues, { BreakpointValue } from "@toolkit/Framework/Common/Utilities/breakpointValues";
import getPreviousBreakpoint from "@toolkit/Framework/Common/Utilities/getPreviousBreakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, PropsWithChildren, useMemo } from "react";
import { css, useTheme } from "styled-components";

export interface TextSeparatorPresentationProps extends ExpandStyles<TextSeparatorStyles> {
    align?: "left" | "center" | "right";
    direction?: "horizontal" | "vertical";
    directionBreakpoint?: BreakpointKey;
    spacing?: BreakpointValue[];
}

export interface TextSeparatorProps extends TextSeparatorPresentationProps {}

export type TextSeparatorStyledProp = StyledProp | undefined | null;

export interface TextSeparatorStyles {
    content?: InjectableCss & { as?: keyof JSX.IntrinsicElements };
    contentHorizontalCss?: TextSeparatorStyledProp;
    contentVerticalCss?: TextSeparatorStyledProp;
    separator?: InjectableCss & { as?: keyof JSX.IntrinsicElements };
    separatorHorizontalCss?: TextSeparatorStyledProp;
    separatorVerticalCss?: TextSeparatorStyledProp;
    text?: TypographyPresentationProps;
    textHorizontalCss?: TextSeparatorStyledProp;
    textVerticalCss?: TextSeparatorStyledProp;
    textLeftCss?: TextSeparatorStyledProp;
    textRightCss?: TextSeparatorStyledProp;
}

export const textSeparatorStyles: TextSeparatorStyles = {
    text: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            color: ${getColor("text.accent")};
        `,
    },
    textHorizontalCss: css`
        width: 100%;
        margin-right: auto;
        margin-left: auto;
    `,
    textVerticalCss: css`
        flex-direction: column;
        height: 100%;
        margin-top: auto;
        margin-bottom: auto;
    `,
    separator: {
        as: "span",
        css: css`
            flex-grow: 1;
            order: 1;
            border: ${pxToRem(1)} solid ${getColor("common.border")};
            border-bottom: 0;
            border-left: 0;
        `,
    },
    separatorHorizontalCss: css`
        border-right: 0;
    `,
    separatorVerticalCss: css`
        border-top: 0;
    `,
    content: {
        as: "span",
        css: css`
            order: 1;
            margin: 1em;
            text-align: center;
        `,
    },
    contentHorizontalCss: css`
        max-width: 80%;
        margin-top: 0;
        margin-bottom: 0;
    `,
    contentVerticalCss: css`
        margin-right: 0;
        margin-left: 0;
    `,
};

const TextSeparator: FC<PropsWithChildren<TextSeparatorProps>> = ({
    align = "center",
    children,
    direction = "horizontal",
    directionBreakpoint,
    spacing,
    extendedStyles,
    overridedStyles,
}) => {
    const baseStyles = useExpandProps(textSeparatorStyles, extendedStyles, overridedStyles);
    const theme = useTheme();
    const hasContent = Boolean(children);

    const directionStyles = (horizontalCss: TextSeparatorStyledProp, verticalCss: TextSeparatorStyledProp) => {
        const currentCss = direction === "horizontal" ? horizontalCss : verticalCss;

        if (directionBreakpoint) {
            const breakpointCss = direction === "horizontal" ? verticalCss : horizontalCss;

            return css`
                ${() => {
                    if (currentCss) {
                        return css`
                            ${breakpoint(getPreviousBreakpoint(directionBreakpoint, theme), "down")} {
                                ${currentCss}
                            }
                        `;
                    }
                }}

                ${() => {
                    if (breakpointCss) {
                        return css`
                            ${breakpoint(directionBreakpoint)} {
                                ${breakpointCss}
                            }
                        `;
                    }
                }}
            `;
        }

        return currentCss;
    };

    const styles = useMemo(() => {
        return mergeProps<TextSeparatorStyles>(baseStyles, {
            text: {
                css: css`
                    ${breakpointValues(spacing, "margin")}
                    ${directionStyles(baseStyles.textHorizontalCss, baseStyles.textVerticalCss)}
                `,
            },
            separator: {
                css: css`
                    ${directionStyles(baseStyles.separatorHorizontalCss, baseStyles.separatorVerticalCss)}
                `,
            },
            content: {
                css: css`
                    ${directionStyles(baseStyles.contentHorizontalCss, baseStyles.contentVerticalCss)}

                    ${() => {
                        if (align === "left") {
                            return css`
                                order: 0;
                                ${directionStyles(
                                    css`
                                        margin-left: 0;
                                        text-align: left;
                                    `,
                                    css`
                                        margin-top: 0;
                                    `,
                                )}
                            `;
                        }

                        if (align === "right") {
                            return css`
                                order: 2;
                                ${directionStyles(
                                    css`
                                        margin-right: 0;
                                        text-align: right;
                                    `,
                                    css`
                                        margin-bottom: 0;
                                    `,
                                )}
                            `;
                        }
                    }}

                    ${() => {
                        if (!hasContent) {
                            return directionStyles(
                                css`
                                    visibility: hidden;
                                    overflow: hidden;
                                    width: 0;
                                    padding: 0;
                                    margin: 0;
                                `,
                                css`
                                    display: none;
                                `,
                            );
                        }
                    }}
                `,
            },
        });
    }, [theme, align, baseStyles, direction, directionBreakpoint, hasContent, spacing]);

    return (
        <Typography aria-hidden={hasContent ? undefined : true} {...styles.text}>
            {align === "center" && hasContent && <StyledWrapper aria-hidden="true" {...styles.separator} />}
            <StyledWrapper {...styles.content}>{children || `\u00A0`}</StyledWrapper>
            <StyledWrapper aria-hidden="true" {...styles.separator} />
        </Typography>
    );
};

export default TextSeparator;
